import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { emptyStateIcon } from '@/components/ui/empty-state-icon';
import { iconMagnifyingGlass, iconTriangleExclamation } from '@/lib/icons';
import { isStaleChunkError, reloadPending } from '@/lib/stale-chunk-reload';
import {
  Link,
  useRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { BRAND } from '@/lib/brand';

const ErrorIcon = emptyStateIcon(iconTriangleExclamation);
const NotFoundIcon = emptyStateIcon(iconMagnifyingGlass);

/**
 * Where a route's error or not-found sits: the page's slot. The nav rail is a
 * fixed <aside> in the same row, so without the page's own margin
 * (app-layout's ml-[256px], ml-16 folded) the message centered on the whole
 * window, under the rail's edge. Only beside a rail: signed out there is none.
 */
const ROUTE_SLOT =
  'flex h-full min-h-[50vh] min-w-0 flex-1 items-center justify-center p-6 text-foreground [aside~&]:ml-[256px] in-data-[sidebar-collapsed="true"]:[aside~&]:ml-16';

export function RootError() {
  return (
    <div
      role='alert'
      className='flex min-h-screen items-center justify-center bg-background p-6 text-foreground'
    >
      <EmptyState
        icon={ErrorIcon}
        headline='Page unavailable'
        action={
          <div className='flex items-center gap-2'>
            <Button size='sm' onClick={() => window.location.reload()}>
              Reload
            </Button>
            <Button asChild variant='outline' size='sm'>
              <a href='/'>Go home</a>
            </Button>
          </div>
        }
      />
    </div>
  );
}

/**
 * One page failed, shown in that page's place: the app shell around it stays,
 * so the sidebar still works and moving to another page clears the error.
 * Without a per-route boundary a page crash replaced the whole app with a
 * blank screen (Replay: Sequences).
 *
 * Try again re-runs the route: the boundary only resets when the match
 * reloads, so a bare `reset` re-threw the same error (Replay: ~7 clicks on
 * Settings). A chunk from an older deploy stays failed until the page
 * reloads, so that case reloads. While that reload is already on its way
 * (main.tsx's vite:preloadError), it shows nothing rather than flash the error.
 */
export function RouteError({ error }: ErrorComponentProps) {
  const router = useRouter();
  if (reloadPending() && isStaleChunkError(error?.message)) return null;
  const tryAgain = () => {
    if (isStaleChunkError(error?.message)) {
      window.location.reload();
      return;
    }
    void router.invalidate();
  };
  return (
    <div role='alert' className={ROUTE_SLOT}>
      <EmptyState
        icon={ErrorIcon}
        headline="This page couldn't load"
        action={
          <div className='flex items-center gap-2'>
            <Button size='sm' onClick={tryAgain}>
              Try again
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={() => window.location.reload()}
            >
              Reload
            </Button>
          </div>
        }
      />
    </div>
  );
}

/**
 * A URL no route matches, shown in the page's place like RouteError. The
 * router's default is a bare <p>Not Found</p> that the shell's flex row put
 * under the nav rail, so a mistyped or stale link (/plays/…) showed a blank
 * page.
 */
export function RouteNotFound() {
  return (
    <div className={ROUTE_SLOT}>
      <EmptyState
        icon={NotFoundIcon}
        headline='Page not found'
        description={`This link doesn't go anywhere in ${BRAND.name}. Check the address, or start from Home.`}
        action={
          <Button asChild size='sm'>
            <Link to='/'>Go home</Link>
          </Button>
        }
      />
    </div>
  );
}
